import { useState } from "react";
import axios from "axios";
import { CalendarCheck, Loader2, MessageCircle, Phone } from "lucide-react";
import { BUSINESS, SERVICES } from "@/constants";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { name: "", phone: "", service: SERVICES[0], date: "", time: "", notes: "" };

export default function Booking() {
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState("idle"); // idle | sending | done | error

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/bookings`, form);
      setStatus("done");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section
      id="book"
      data-testid="booking-section"
      className="py-28 sm:py-36 bg-surface/40 border-y border-line"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-16 lg:gap-24 items-start">
        <Reveal>
          <span className="text-xs font-semibold tracking-[0.3em] uppercase text-gold">
            Reserve Your Chair
          </span>
          <h2 className="mt-5 font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-bone leading-[1.05]">
            Book your
            <em className="text-gold-gradient italic"> glow-up.</em>
          </h2>
          <p className="mt-6 text-sand leading-relaxed max-w-md">
            Tell us what you need and when — we'll confirm your appointment on
            WhatsApp within minutes. Prefer to talk? We're one tap away.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href={BUSINESS.whatsappWithText(
                "Hi Shine Space! I'd like to book an appointment."
              )}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="booking-whatsapp-button"
              className="flex items-center gap-2.5 text-sm font-semibold tracking-widest uppercase bg-[#1EBE5D] text-white px-7 py-4 rounded-full hover:bg-[#22d068] transition-colors duration-300"
            >
              <MessageCircle size={16} /> WhatsApp
            </a>
            <a
              href={BUSINESS.phoneTel}
              data-testid="booking-call-button"
              className="flex items-center gap-2.5 text-sm font-semibold tracking-widest uppercase text-bone border border-bone/25 px-7 py-4 rounded-full hover:border-gold hover:text-gold transition-colors duration-300"
            >
              <Phone size={16} /> {BUSINESS.phoneDisplay}
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          {status === "done" ? (
            <div
              data-testid="booking-success"
              className="border border-gold/40 rounded-sm p-10 sm:p-14 text-center bg-ink"
            >
              <span className="mx-auto w-16 h-16 rounded-full bg-gold/15 text-gold flex items-center justify-center">
                <CalendarCheck size={26} />
              </span>
              <h3 className="mt-6 font-serif text-3xl text-bone">
                Request received, {form.name.split(" ")[0]}.
              </h3>
              <p className="mt-3 text-sand leading-relaxed">
                We'll confirm your {form.service.split("—")[0].trim()} appointment
                shortly. Want an instant confirmation?
              </p>
              <a
                href={BUSINESS.whatsappWithText(
                  `Hi Shine Space! I just requested a booking for ${form.service} on ${form.date} at ${form.time}. Name: ${form.name}.`
                )}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="booking-success-whatsapp"
                className="mt-8 inline-flex items-center gap-2.5 text-sm font-semibold tracking-widest uppercase bg-[#1EBE5D] text-white px-7 py-4 rounded-full hover:bg-[#22d068] transition-colors duration-300"
              >
                <MessageCircle size={16} /> Confirm on WhatsApp
              </a>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="booking-form"
              className="border border-line rounded-sm p-8 sm:p-10 bg-ink space-y-5"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <input
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your name"
                  data-testid="booking-name-input"
                  className="lux-field rounded-sm"
                />
                <input
                  required
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="Phone / WhatsApp number"
                  data-testid="booking-phone-input"
                  className="lux-field rounded-sm"
                />
              </div>
              <select
                value={form.service}
                onChange={set("service")}
                data-testid="booking-service-select"
                className="lux-field rounded-sm"
              >
                {SERVICES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <div className="grid sm:grid-cols-2 gap-5">
                <input
                  required
                  type="date"
                  value={form.date}
                  onChange={set("date")}
                  data-testid="booking-date-input"
                  className="lux-field rounded-sm"
                />
                <input
                  required
                  type="time"
                  value={form.time}
                  onChange={set("time")}
                  data-testid="booking-time-input"
                  className="lux-field rounded-sm"
                />
              </div>
              <textarea
                rows={3}
                value={form.notes}
                onChange={set("notes")}
                placeholder="Anything we should know? (optional)"
                data-testid="booking-notes-input"
                className="lux-field rounded-sm resize-none"
              />
              {status === "error" && (
                <p
                  data-testid="booking-error"
                  className="text-sm text-red-400"
                >
                  Something went wrong — please try again or WhatsApp us
                  directly.
                </p>
              )}
              <button
                type="submit"
                disabled={status === "sending"}
                data-testid="booking-submit-button"
                className="w-full flex items-center justify-center gap-2.5 text-sm font-semibold tracking-widest uppercase bg-gold text-ink py-4.5 px-6 py-4 rounded-full hover:bg-goldlight disabled:opacity-60 transition-colors duration-300"
              >
                {status === "sending" ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <CalendarCheck size={16} />
                )}
                {status === "sending" ? "Sending…" : "Request Appointment"}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
